/* eslint-disable react/jsx-props-no-spreading */
import MicromagConsent from '@micromag/consent';
import classNames from 'classnames';
import React, { useEffect, useCallback, useRef } from 'react';

import useKeyboardKeys, { KEYS } from '../../hooks/useKeyboardKeys';

import { useModal } from '../../contexts/ModalContext';

import styles from '../../styles/modals/consent.module.css';

const TITLE = 'Gestion des cookies';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

const cookies = ['functionality_storage'];

function getFocusableElements(element: HTMLElement | null): HTMLElement[] {
    if (element === null) {
        return [];
    }
    return Array.from(element.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (node) => node.offsetWidth > 0 || node.offsetHeight > 0 || node === document.activeElement,
    );
}

interface ConsentProps {
    className?: string | null;
    /**
     * Identifiant de l'élément qui doit reprendre le focus à la fermeture quand
     * le déclencheur d'origine a été démonté avec la modale. `null` quand la
     * modale s'est ouverte d'elle-même : le focus est alors laissé en place.
     */
    returnFocusId?: string | null;
}

function Consent({ className = null, returnFocusId = null, choices = cookies }: ConsentProps) {
    const modalRef = useRef<HTMLDivElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const { modal = null, setModal, unsetModal } = useModal();

    const onClose = useCallback(() => {
        unsetModal();
        setModal(null);
    }, [setModal, unsetModal]);

    const visible = modal !== null;

    useEffect(() => {
        if (modal !== null) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [modal]);

    // Le focus entre dans la boîte de dialogue à l'ouverture et retourne à son
    // point de départ à la fermeture (WCAG 2.4.3 / RGAA 7.3).
    useEffect(() => {
        const { activeElement } = document;
        previouslyFocusedRef.current = activeElement instanceof HTMLElement ? activeElement : null;

        const modalElement = modalRef.current;
        if (modalElement !== null) {
            const [firstFocusable = null] = getFocusableElements(modalElement);
            (firstFocusable || modalElement).focus();
        }

        return () => {
            const previous = previouslyFocusedRef.current;
            if (previous !== null && previous !== document.body && document.contains(previous)) {
                previous.focus();
                return;
            }
            // Le bouton flottant d'ouverture est démonté en même temps que la
            // modale : on rend le focus à celui qui prend sa place.
            if (returnFocusId === null) {
                return;
            }
            const fallback = document.getElementById(returnFocusId);
            if (fallback !== null) {
                fallback.focus();
            }
        };
    }, [returnFocusId]);

    // Le focus reste dans la boîte de dialogue tant qu'elle est ouverte.
    const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Tab') {
            return;
        }
        const focusables = getFocusableElements(modalRef.current);
        if (focusables.length === 0) {
            event.preventDefault();
            return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }, []);

    useKeyboardKeys({
        [KEYS.ESCAPE]: onClose,
    });

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.visible]: visible,
                    [className]: className !== null,
                },
            ])}
        >
            <div
                className={styles.wrapper}
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                aria-label={TITLE}
                tabIndex={-1}
                onKeyDown={onKeyDown}
            >
                <div className={styles.inner}>
                    <MicromagConsent
                        className={styles.consent}
                        onClose={onClose}
                        choices={choices}
                        withoutChoices
                        labels={{
                            title: TITLE,
                            description:
                                "Ce site utilise des cookies techniques nécessaires à son fonctionnement ainsi qu'un dispositif de mesure d'audience. Afin de mesurer la fréquentation et d'améliorer la qualité du service, des statistiques anonymisées et limitées à la seule mesure d'audience sont réalisées conformément aux recommandations de la CNIL. Ces données ne sont ni utilisées à des fins publicitaires, ni partagées avec des tiers à ces fins.",
                            terms: 'Lire notre politique de gestion des cookies',
                        }}
                    />
                </div>
            </div>
            <div className={styles.lightBox} aria-hidden="true" />
        </div>
    );
}

export default Consent;
